/**
 * CV autofill: merges what POST /users/{id}/cv/extract read from the CV into the
 * profile form.
 *
 * It only fills what is empty. A value the user typed or saved is never replaced:
 * years and role only when blank, a technology/sector only while it is '-' (nulo),
 * and experiences, titles and certificates are appended when the form does not
 * already list them (same company and start / same name). The result also says
 * which entries came from the CV so the page can mark them for review; nothing is
 * saved until the user presses "Guardar Perfil".
 */

import type { CvExtraction, Experience } from '../../types/cv'

interface NamedEntry {
    name: string
}

interface SkillEntry {
    skillId: string
    category: string
    name: string
    level: string
}

export interface AutofillForm<C extends NamedEntry, T extends NamedEntry, S extends SkillEntry> {
    yearsExperience: string
    jobRole: string
    experiences: Experience[]
    certificates: C[]
    titles: T[]
    techSkills: S[]
    sectorSkills: S[]
}

export interface AutofillCounts {
    years: boolean
    role: boolean
    experiences: number
    titles: number
    certificates: number
    techSkills: number
    sectorSkills: number
}

export interface AutofillResult<C extends NamedEntry, T extends NamedEntry, S extends SkillEntry> {
    form: AutofillForm<C, T, S>
    /** 'years_experience' / 'job_role' when the CV filled them. */
    filledFields: string[]
    /** The experience/title/certificate objects that came from the CV. */
    newEntries: object[]
    /** skillId → the CV evidence for every technology/sector level the CV set. */
    skillEvidence: Record<string, string>
    counts: AutofillCounts
}

export const MAX_EXPERIENCES = 30
export const MAX_TITLES = 10
export const MAX_CERTIFICATES = 10

/** Case-, accent- and spacing-insensitive form of a name, to spot an entry the form already has. */
export const normalizeName = (value?: string | null): string =>
    (value || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()

const experienceKey = (exp: Experience) => `${normalizeName(exp.company)}|${exp.start_date || ''}`

const orUndefined = (value?: string | null) => value || undefined

export function applyCvExtraction<C extends NamedEntry, T extends NamedEntry, S extends SkillEntry>(
    form: AutofillForm<C, T, S>,
    extraction: CvExtraction,
    jobRoles: string[],
): AutofillResult<C, T, S> {
    const filledFields: string[] = []
    const newEntries: object[] = []
    const skillEvidence: Record<string, string> = {}

    let yearsExperience = form.yearsExperience
    if (!yearsExperience.trim() && extraction.years_experience != null) {
        yearsExperience = String(extraction.years_experience)
        filledFields.push('years_experience')
    }

    let jobRole = form.jobRole
    if (!jobRole && extraction.job_role && jobRoles.includes(extraction.job_role)) {
        jobRole = extraction.job_role
        filledFields.push('job_role')
    }

    const experiences = [...form.experiences]
    const knownExperiences = new Set(experiences.map(experienceKey))
    let addedExperiences = 0
    for (const exp of extraction.experiences || []) {
        if (experiences.length >= MAX_EXPERIENCES) break
        if (!exp.company?.trim() || knownExperiences.has(experienceKey(exp))) continue
        const entry: Experience = {
            company: exp.company,
            position: exp.position || '',
            start_date: exp.start_date || '',
            end_date: exp.current ? '' : exp.end_date || '',
            current: Boolean(exp.current),
            description: exp.description || '',
        }
        knownExperiences.add(experienceKey(entry))
        experiences.push(entry)
        newEntries.push(entry)
        addedExperiences++
    }

    const titles = [...form.titles]
    const knownTitles = new Set(titles.map((t) => normalizeName(t.name)))
    let addedTitles = 0
    for (const title of extraction.titles || []) {
        if (titles.length >= MAX_TITLES) break
        if (!title.name?.trim() || knownTitles.has(normalizeName(title.name))) continue
        const entry = {
            name: title.name,
            institution: orUndefined(title.institution),
            date_obtained: orUndefined(title.date_obtained),
        } as unknown as T
        knownTitles.add(normalizeName(title.name))
        titles.push(entry)
        newEntries.push(entry)
        addedTitles++
    }

    const certificates = [...form.certificates]
    const knownCertificates = new Set(certificates.map((c) => normalizeName(c.name)))
    let addedCertificates = 0
    for (const cert of extraction.certificates || []) {
        if (certificates.length >= MAX_CERTIFICATES) break
        if (!cert.name?.trim() || knownCertificates.has(normalizeName(cert.name))) continue
        const entry = {
            name: cert.name,
            issuer: orUndefined(cert.issuer),
            date_obtained: orUndefined(cert.date_obtained),
            expiry_date: orUndefined(cert.expiry_date),
            credential_id: orUndefined(cert.credential_id),
        } as unknown as C
        knownCertificates.add(normalizeName(cert.name))
        certificates.push(entry)
        newEntries.push(entry)
        addedCertificates++
    }

    const techLevels = new Map(
        (extraction.tech_skills || []).map((s) => [`${s.category}|${s.name}`, s] as const),
    )
    let setTechSkills = 0
    const techSkills = form.techSkills.map((skill) => {
        const suggestion = techLevels.get(`${skill.category}|${skill.name}`)
        if (!suggestion || skill.level !== 'nulo') return skill
        skillEvidence[skill.skillId] = suggestion.evidence || ''
        setTechSkills++
        return { ...skill, level: suggestion.level }
    })

    const sectorLevels = new Map((extraction.sector_skills || []).map((s) => [s.name, s] as const))
    let setSectorSkills = 0
    const sectorSkills = form.sectorSkills.map((skill) => {
        const suggestion = sectorLevels.get(skill.name)
        if (!suggestion || skill.level !== 'nulo') return skill
        skillEvidence[skill.skillId] = suggestion.evidence || ''
        setSectorSkills++
        return { ...skill, level: suggestion.level }
    })

    return {
        form: { yearsExperience, jobRole, experiences, certificates, titles, techSkills, sectorSkills },
        filledFields,
        newEntries,
        skillEvidence,
        counts: {
            years: filledFields.includes('years_experience'),
            role: filledFields.includes('job_role'),
            experiences: addedExperiences,
            titles: addedTitles,
            certificates: addedCertificates,
            techSkills: setTechSkills,
            sectorSkills: setSectorSkills,
        },
    }
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** One line for the user: what the CV filled in, or that it added nothing new. */
export function describeAutofill(counts: AutofillCounts): string {
    const parts: string[] = []
    if (counts.years) parts.push('años de experiencia')
    if (counts.role) parts.push('rol')
    if (counts.experiences) parts.push(plural(counts.experiences, 'experiencia', 'experiencias'))
    if (counts.titles) parts.push(plural(counts.titles, 'título', 'títulos'))
    if (counts.certificates) parts.push(plural(counts.certificates, 'certificado', 'certificados'))
    if (counts.techSkills) parts.push(plural(counts.techSkills, 'tecnología', 'tecnologías'))
    if (counts.sectorSkills) parts.push(plural(counts.sectorSkills, 'sector', 'sectores'))
    if (parts.length === 0) {
        return 'Tu CV no aporta datos nuevos: el perfil ya tenía todo lo que hemos encontrado.'
    }
    const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`
    return `Rellenado desde tu CV: ${list}. Revisa lo marcado con ✨ y pulsa «Guardar Perfil».`
}

/** YYYY-MM, the shape the backend takes for an experience month. */
export const isYearMonth = (value?: string | null): boolean => !value || /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
